import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

interface WarningHighlightContextValue {
  highlight: (rowId: string, field: string[]) => void
  clear: () => void
  registerCellRef: (
    rowId: string,
    field: string,
    el: HTMLDivElement | null
  ) => void
  highlightedCells: Record<string, true>
}

const WarningHighlightContext = createContext<WarningHighlightContextValue | null>(null)

export const WarningHighlightProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Stores references to table cells
  const cellRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())
  // Tracks currently highlighted cells
  const [highlightedCells, setHighlightedCells] = useState<Record<string, true>>({})

  // Registers cell references for highlighting into cellRefs
  const registerCellRef = useCallback((rowId: string, field: string, el: HTMLDivElement | null): void => {
    const key = `${rowId}:${field}`
    if (el) {
      cellRefs.current.set(key, el)
    } else {
      cellRefs.current.delete(key)
    }
  }, [])

  // Adds all corresponding cells to highlightedCells state
  const highlight = useCallback((rowId: string, fields: string[]): void => {
    setHighlightedCells((prev) => {
      const updatedCells = { ...prev }
      fields.forEach((field) => {
        if (cellRefs.current.has(`${rowId}:${field}`)) {
          updatedCells[`${rowId}:${field}`] = true
        }
      })
      return updatedCells
    })
  }, [])

  const clear = useCallback((): void => {
    setHighlightedCells({})
  }, [])

  const contextValues = useMemo(() => ({
    highlight,
    clear,
    registerCellRef,
    highlightedCells
  }), [highlight, clear, registerCellRef, highlightedCells])

  return (
    <WarningHighlightContext.Provider
      value={contextValues}
    >
      {children}
    </WarningHighlightContext.Provider>
  )
}

export const useWarningHighlight = (): WarningHighlightContextValue => {
  const context = useContext(WarningHighlightContext)
  if (!context) {
    throw new Error(
      'useWarningHighlight must be used within a WarningHighlightProvider'
    )
  }
  return context
}

export const useOptionalWarningHighlight = (): WarningHighlightContextValue | null => {
  const context = useContext(WarningHighlightContext)
  return context
}
